import { type ComponentType, Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { FullPageSpinner } from '@/components/feedback/FullPageSpinner';
import { RootLayout } from '@/components/layout/RootLayout';
import { RequireAuth, RequirePermission } from '@/lib/auth/guards';
import { Permission } from '@/lib/auth/permissions';
import { useAuth } from '@/lib/auth/useAuth';
// Home is eager for a fast first paint / LCP; everything else is code-split.
import { HomePage } from '@/pages/HomePage';

/**
 * The home page is the public marketing site. Signed-in STAFF are sent straight
 * to their dashboard — they only browse the public site once logged out. Regular
 * members have no dashboard, so they see the public home normally.
 */
function HomeRoute() {
  const { status, isStaff } = useAuth();
  if (status === 'authenticated' && isStaff) return <Navigate to="/admin" replace />;
  return <HomePage />;
}

function named<M, K extends keyof M>(loader: () => Promise<M>, name: K) {
  return lazy(() => loader().then((m) => ({ default: m[name] as ComponentType })));
}

// Public
const AboutPage = named(() => import('@/pages/CmsPage'), 'AboutPage');
const ProgramsPage = named(() => import('@/pages/CmsPage'), 'ProgramsPage');
const EventsPage = named(() => import('@/pages/EventsPage'), 'EventsPage');
const EventDetailPage = named(() => import('@/pages/EventDetailPage'), 'EventDetailPage');
const ResourcesPage = named(() => import('@/pages/ResourcesPage'), 'ResourcesPage');
const ResourceDetailPage = named(() => import('@/pages/ResourceDetailPage'), 'ResourceDetailPage');
const NewsPage = named(() => import('@/pages/NewsPage'), 'NewsPage');
const NewsDetailPage = named(() => import('@/pages/NewsDetailPage'), 'NewsDetailPage');
const GetHelpPage = named(() => import('@/pages/GetHelpPage'), 'GetHelpPage');
const JoinPage = named(() => import('@/pages/JoinPage'), 'JoinPage');
const DonatePage = named(() => import('@/pages/DonatePage'), 'DonatePage');
const DonateThankYouPage = named(() => import('@/pages/DonateThankYouPage'), 'DonateThankYouPage');
const ContactPage = named(() => import('@/pages/ContactPage'), 'ContactPage');
const GalleryPage = named(() => import('@/pages/GalleryPage'), 'GalleryPage');
const AlbumDetailPage = named(() => import('@/pages/AlbumDetailPage'), 'AlbumDetailPage');
const SearchPage = named(() => import('@/pages/SearchPage'), 'SearchPage');
const AccountPage = named(() => import('@/pages/AccountPage'), 'AccountPage');
const ProfilePage = named(() => import('@/pages/ProfilePage'), 'ProfilePage');
const NotificationsPage = named(() => import('@/pages/NotificationsPage'), 'NotificationsPage');
const NewsletterConfirmPage = named(() => import('@/pages/NewsletterConfirmPage'), 'NewsletterConfirmPage');
const NewsletterUnsubscribePage = named(() => import('@/pages/NewsletterConfirmPage'), 'NewsletterUnsubscribePage');
const NotFoundPage = named(() => import('@/pages/ErrorPages'), 'NotFoundPage');
const ForbiddenPage = named(() => import('@/pages/ErrorPages'), 'ForbiddenPage');

// Auth
const LoginPage = named(() => import('@/pages/auth/LoginPage'), 'LoginPage');
const RegisterPage = named(() => import('@/pages/auth/RegisterPage'), 'RegisterPage');
const ForgotPasswordPage = named(() => import('@/pages/auth/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = named(() => import('@/pages/auth/ResetPasswordPage'), 'ResetPasswordPage');
const VerifyEmailPage = named(() => import('@/pages/auth/VerifyEmailPage'), 'VerifyEmailPage');

// Admin (permission-gated, separate chrome)
const AdminLayout = named(() => import('@/features/admin/AdminLayout'), 'AdminLayout');
const AdminDashboardPage = named(() => import('@/pages/admin/AdminDashboardPage'), 'AdminDashboardPage');
const AdminSupportRequestsPage = named(() => import('@/pages/admin/AdminSupportRequestsPage'), 'AdminSupportRequestsPage');
const AdminDonationsPage = named(() => import('@/pages/admin/AdminDonationsPage'), 'AdminDonationsPage');
const AdminMembersPage = named(() => import('@/pages/admin/AdminMembersPage'), 'AdminMembersPage');
const AdminVolunteersPage = named(() => import('@/pages/admin/AdminVolunteersPage'), 'AdminVolunteersPage');
const AdminContactPage = named(() => import('@/pages/admin/AdminContactPage'), 'AdminContactPage');
const AdminNewsPage = named(() => import('@/pages/admin/AdminNewsPage'), 'AdminNewsPage');
const AdminEventsPage = named(() => import('@/pages/admin/AdminEventsPage'), 'AdminEventsPage');
const AdminResourcesPage = named(() => import('@/pages/admin/AdminResourcesPage'), 'AdminResourcesPage');
const AdminPagesPage = named(() => import('@/pages/admin/AdminPagesPage'), 'AdminPagesPage');
const AdminGalleryPage = named(() => import('@/pages/admin/AdminGalleryPage'), 'AdminGalleryPage');
const AdminUsersPage = named(() => import('@/pages/admin/AdminUsersPage'), 'AdminUsersPage');

const s = (node: React.ReactNode) => <Suspense fallback={<FullPageSpinner />}>{node}</Suspense>;

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { index: true, element: <HomeRoute /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'programs', element: <ProgramsPage /> },
      { path: 'events', element: <EventsPage /> },
      { path: 'events/:eventId', element: <EventDetailPage /> },
      { path: 'resources', element: <ResourcesPage /> },
      { path: 'resources/:resourceId', element: <ResourceDetailPage /> },
      { path: 'news', element: <NewsPage /> },
      { path: 'news/:slug', element: <NewsDetailPage /> },
      { path: 'get-help', element: <GetHelpPage /> },
      { path: 'join', element: <JoinPage /> },
      { path: 'donate', element: <DonatePage /> },
      { path: 'donate/thank-you', element: <DonateThankYouPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'gallery', element: <GalleryPage /> },
      { path: 'gallery/:albumId', element: <AlbumDetailPage /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'newsletter/confirm', element: <NewsletterConfirmPage /> },
      { path: 'newsletter/unsubscribe', element: <NewsletterUnsubscribePage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'verify-email', element: <VerifyEmailPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: 'account', element: <AccountPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'notifications', element: <NotificationsPage /> },
        ],
      },
      { path: '403', element: <ForbiddenPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: '/admin',
    element: (
      <RequirePermission permission={Permission.ANALYTICS_VIEW}>{s(<AdminLayout />)}</RequirePermission>
    ),
    children: [
      { index: true, element: s(<AdminDashboardPage />) },
      { path: 'profile', element: s(<ProfilePage />) },
      { path: 'notifications', element: s(<NotificationsPage />) },
      { path: 'news', element: s(<AdminNewsPage />) },
      { path: 'events', element: s(<AdminEventsPage />) },
      { path: 'resources', element: s(<AdminResourcesPage />) },
      { path: 'pages', element: s(<AdminPagesPage />) },
      { path: 'gallery', element: s(<AdminGalleryPage />) },
      { path: 'users', element: s(<AdminUsersPage />) },
      { path: 'support-requests', element: s(<AdminSupportRequestsPage />) },
      { path: 'donations', element: s(<AdminDonationsPage />) },
      { path: 'members', element: s(<AdminMembersPage />) },
      { path: 'volunteers', element: s(<AdminVolunteersPage />) },
      { path: 'contact', element: s(<AdminContactPage />) },
    ],
  },
]);
